/**
 * Unified API Client
 *
 * Drop-in replacement for scattered fetch() calls across the codebase:
 * real fetch() with credentials: 'include' + CSRF headers on writes.
 */

import { getCsrfToken } from './csrfService';
import { takePrefetched } from './siteBoot';

// Session expiry detection — matches conversationApi.ts pattern
export class SessionExpiredError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

// ─── Core fetch replacement ───────────────────────────────────────────

/**
 * Unified fetch replacement. Signature matches native fetch() so existing
 * call sites can swap with minimal changes:
 *
 *   // Before
 *   const res = await fetch('/api/conversations', { credentials: 'include', ... });
 *
 *   // After
 *   const res = await apiFetch('/api/conversations', { ... });
 */
export async function apiFetch(
  path: string,
  options?: RequestInit,
): Promise<Response> {
  const method = options?.method?.toUpperCase() || 'GET';

  // ── A public GET the server's inline script already started is reused ──
  if (method === 'GET' && !options?.body) {
    const prefetched = await takePrefetched(path);
    if (prefetched) return prefetched;
  }

  // ── Standard fetch with cookies + CSRF ──
  return fetchWithCredentials(path, options, method);
}

/**
 * Convenience wrapper that parses JSON and throws on error.
 * Mirrors the pattern from conversationApi.ts apiFetch<T>().
 */
export async function apiFetchJson<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const res = await apiFetch(path, options);

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const errorMessage =
      (err as { error?: string }).error || `API error ${res.status}`;

    if (res.status === 401) {
      throw new SessionExpiredError(errorMessage);
    }
    throw new Error(errorMessage);
  }

  return res.json() as Promise<T>;
}

// ─── Cookie + CSRF auth ───────────────────────────────────────────────

function fetchWithCredentials(
  path: string,
  options: RequestInit | undefined,
  method: string,
): Promise<Response> {
  const isWrite = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  const csrfToken = getCsrfToken();

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };

  // Only set Content-Type if not already set (FormData sets its own)
  if (!headers['Content-Type'] && typeof options?.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  if (isWrite && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  return fetch(path, {
    ...options,
    method,
    credentials: 'include',
    headers,
  });
}

// ─── Upload helper (replaces XHR pattern) ─────────────────────────────

export interface UploadOptions {
  method?: string;
  headers?: Record<string, string>;
  onProgress?: (loaded: number, total: number) => void;
  /** Aborting stops the transfer (the promise rejects with an AbortError). */
  signal?: AbortSignal;
}

/**
 * Upload a file with progress tracking. Replaces the XMLHttpRequest
 * pattern in mediaUploadService.ts and audioUploadService.ts.
 */
export function apiUpload(
  path: string,
  body: FormData | ArrayBuffer | Blob,
  options: UploadOptions = {},
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'POST', path);
    xhr.withCredentials = true;

    // CSRF
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      xhr.setRequestHeader('X-CSRF-Token', csrfToken);
    }

    // Custom headers
    if (options.headers) {
      for (const [key, value] of Object.entries(options.headers)) {
        xhr.setRequestHeader(key, value);
      }
    }

    if (options.onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && options.onProgress) {
          options.onProgress(e.loaded, e.total);
        }
      };
    }

    xhr.onload = () => {
      resolve(
        new Response(xhr.responseText, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: { 'Content-Type': 'application/json' },
        }),
      );
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    if (options.signal) {
      const signal = options.signal;
      const onAbort = () => { xhr.abort(); reject(new DOMException('aborted', 'AbortError')); };
      if (signal.aborted) { onAbort(); return; }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    xhr.send(body);
  });
}
